import { intersection } from "ramda";
import { Context } from "common/types/context";
import { Tenant } from "common/types/tenants";
import {
  isAdminUserType,
  isFlukeMobileUser,
  isFlukeMobileUserType,
  isRequestorUserType,
  isUserEnabled,
  isVendorUserType,
} from "common/types/users";
import {
  defaultUser,
  flukeMobileUserType,
  fullUserTypes,
  requestorUserType,
  User,
  UserType,
  vendorUserType,
} from "common/functions/users";
import { validatePassword } from "common/api/authentication/user";
import { checkFormValidation } from "common/form/functions/validation";
import { FormValidation } from "common/form/types";
import type { PasswordPolicy, SecuritySettings } from "common/types/settings";
import { email } from "common/validate";
import { SelectorOption } from "common/widgets/selector/types";
import { Role } from "common/types/roles";
import { isGroupedOption } from "common/vendor-wrappers/react-select/functions";
import { UsersFormValue } from "./types";

export const getUserMaxTotal = (tenant: Tenant) => {
  const {
    maxUsers = Infinity,
    maxRequestUsers = Infinity,
    maxVendorUsers = Infinity,
    maxFlukeMobileUsers = 0,
  } = tenant;
  return maxUsers + maxRequestUsers + maxFlukeMobileUsers + maxVendorUsers;
};

const isUserLimited = (tenant: Tenant) => {
  return tenant.userMode === "Named" && getUserMaxTotal(tenant) > 0;
};

export const shouldHideNew = (context: Context) => {
  const { enabledUsers, tenant } = context;

  return (
    isFlukeMobileUserType(context.userTypes) ||
    (isUserLimited(tenant) && enabledUsers.total >= getUserMaxTotal(tenant))
  );
};

const checkMaxUsers = (
  referenceTypes: UserType[],
  userTypes: UserType[],
  usersQuantity: number,
  maxQuantity: number,
) =>
  intersection(referenceTypes, userTypes).length > 0 &&
  usersQuantity < (maxQuantity || Infinity);

export const canRestore =
  ({ enabledUsers, tenant }: Context) =>
  (user: User) => {
    return (
      !isUserEnabled(user) &&
      (!isUserLimited(tenant) ||
        checkMaxUsers(
          requestorUserType,
          user.userTypes,
          enabledUsers.requestor,
          tenant.maxRequestUsers,
        ) ||
        checkMaxUsers(
          fullUserTypes,
          user.userTypes,
          enabledUsers.full,
          tenant.maxUsers,
        ) ||
        checkMaxUsers(
          flukeMobileUserType,
          user.userTypes,
          enabledUsers.flukeMobile,
          tenant.maxFlukeMobileUsers,
        ) ||
        checkMaxUsers(
          vendorUserType,
          user.userTypes,
          enabledUsers.vendor,
          tenant.maxVendorUsers,
        ))
    );
  };

export const getLicenseMessage = (context: Context) => {
  const { enabledUsers, tenant } = context;

  if (!enabledUsers || !tenant || !isUserLimited(tenant)) return "";

  const { full = 0, requestor = 0, flukeMobile = 0, vendor = 0 } = enabledUsers;
  const { maxUsers, maxRequestUsers, maxFlukeMobileUsers, maxVendorUsers } =
    tenant;

  const x5Values = [
    { title: _("Full users"), current: full, max: maxUsers },
    { title: _("Requestor"), current: requestor, max: maxRequestUsers },
  ];

  const flukeMobileValue = context.isEnabledFeature("flukeMobile")
    ? [
        {
          title: _("Fluke Mobile"),
          current: flukeMobile,
          max: maxFlukeMobileUsers,
        },
      ]
    : [];

  const vendorValue = context.isEnabledFeature("vendorPortal")
    ? [{ title: _("Vendor"), current: vendor, max: maxVendorUsers }]
    : [];

  return x5Values
    .concat(flukeMobileValue, vendorValue)
    .filter((v) => v.max)
    .map(({ title, current, max }) => `${title}: ${current}/${max}`)
    .join(", ");
};

export const isValid = (
  policy: PasswordPolicy,
  value: UsersFormValue,
  formValidation?: FormValidation,
): boolean => {
  if (!value || !value.user) return false;
  const { user } = value;

  const {
    userName,
    id,
    password,
    passwordConfirm,
    roleId,
    siteName,
    userTypes,
  } = user;

  return !!(
    userName &&
    checkFormValidation(formValidation) &&
    email(userName) &&
    (id || password) &&
    password === passwordConfirm &&
    validatePassword(policy, password, userName) === "" &&
    (isAdminUserType(userTypes) || roleId >= 0) &&
    siteName
  );
};

export const getRoleOptionLabel = (option: SelectorOption<Role>) =>
  isGroupedOption(option) ? option.label : option.name;

export const getHintForUserType = (userType: UserType[]) => {
  if (isVendorUserType(userType)) {
    return _(
      "Chosen User Type: Vendor/Contractor Portal User will create a User just with access to the Vendor Portal",
    );
  }
  if (isRequestorUserType(userType)) {
    return _(
      "Chosen User Type: Requestor will create a User just with access to Requestor Portal",
    );
  }

  return undefined;
};

export const getLoginMethodHint = (user: User) => {
  const mainMessage = _(
    "Additional login method can be overwritten and configured in {SECURITY_SETTINGS}.",
  );
  const tempMessage = isFlukeMobileUser(user)
    ? _("Feature is not supported on the Mobile App yet.")
    : "";

  return `${mainMessage} ${tempMessage}`.trim();
};

export const getDefaultUserValue = (
  settings: SecuritySettings,
): UsersFormValue => ({
  siteOpen: false,
  user: {
    ...defaultUser,
    allowPasswordLogin: settings?.ssoPolicy?.loginMethod === "allUsers",
  },
});
